import { useState, useEffect } from 'react';
import { api } from '../../utils/api';
import { formatCurrency, formatPercent, getMarginStatus } from '../../utils/formatters';
import { TrendingUp, Building2, Users, ChevronLeft, ChevronRight, Calendar, AlertTriangle, PieChart } from 'lucide-react';

function ReportCard({ title, icon: Icon, children, action }) {
  return (
    <div className="card">
      <div className="card-header flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon className="w-5 h-5 text-hub-600" />
          <h2 className="font-semibold">{title}</h2>
        </div>
        {action}
      </div>
      <div className="card-body">{children}</div>
    </div>
  );
}

function MonthPicker({ value, onChange }) {
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  
  const currentYear = new Date().getFullYear();
  const years = [currentYear - 2, currentYear - 1, currentYear, currentYear + 1];
  
  return (
    <div className="flex items-center gap-2">
      <select
        value={value.month}
        onChange={(e) => onChange({ ...value, month: parseInt(e.target.value) })}
        className="input py-1 text-sm"
      >
        {months.map((m, i) => (
          <option key={i} value={i}>{m}</option>
        ))}
      </select>
      <select
        value={value.year}
        onChange={(e) => onChange({ ...value, year: parseInt(e.target.value) })}
        className="input py-1 text-sm"
      >
        {years.map(y => (
          <option key={y} value={y}>{y}</option>
        ))}
      </select>
    </div>
  );
}

function SimpleBarChart({ data, valueKey, labelKey, maxValue }) {
  const max = maxValue || Math.max(...data.map(d => d[valueKey]));
  
  return (
    <div className="space-y-2">
      {data.map((item, i) => (
        <div key={i} className="flex items-center gap-3">
          <div className="w-20 text-xs text-gray-500 truncate">{item[labelKey]}</div>
          <div className="flex-1 h-6 bg-gray-100 rounded overflow-hidden">
            <div 
              className={`h-full rounded transition-all duration-300 ${
                item.marginPercent >= 30 ? 'bg-green-500' :
                item.marginPercent >= 25 ? 'bg-yellow-500' : 'bg-red-500'
              }`}
              style={{ width: `${(item[valueKey] / max) * 100}%` }}
            />
          </div>
          <div className="w-16 text-right text-sm font-medium">
            {formatPercent(item.marginPercent)}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function AdminReports() {
  const now = new Date();
  const [selectedMonth, setSelectedMonth] = useState({ month: now.getMonth(), year: now.getFullYear() });
  const [monthData, setMonthData] = useState([]);
  const [clientData, setClientData] = useState([]);
  const [contractorData, setContractorData] = useState([]);
  const [clientConcentration, setClientConcentration] = useState(null);
  const [selectedMonthDetail, setSelectedMonthDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/dashboard/reports/margin-by-month?months=12'),
      api.get('/dashboard/reports/margin-by-client'),
      api.get('/dashboard/reports/margin-by-contractor'),
      api.get('/dashboard/reports/client-concentration')
    ]).then(([m, c, co, cc]) => {
      setMonthData(m);
      setClientData(c);
      setContractorData(co);
      setClientConcentration(cc);
    }).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadMonthDetail();
  }, [selectedMonth]);

  const loadMonthDetail = async () => {
    const startDate = new Date(selectedMonth.year, selectedMonth.month, 1);
    const endDate = new Date(selectedMonth.year, selectedMonth.month + 1, 0);
    
    try {
      const [clients, contractors] = await Promise.all([
        api.get(`/dashboard/reports/margin-by-client?startDate=${startDate.toISOString()}&endDate=${endDate.toISOString()}`),
        api.get(`/dashboard/reports/margin-by-contractor?startDate=${startDate.toISOString()}&endDate=${endDate.toISOString()}`)
      ]);
      
      // Calculate totals for the month
      const totalRevenue = clients.reduce((sum, c) => sum + c.totalInvoice, 0);
      const totalPayout = clients.reduce((sum, c) => sum + c.totalPayout, 0);
      const totalGigs = clients.reduce((sum, c) => sum + c.gigCount, 0);
      const productionFee = totalRevenue - totalPayout;
      const margin = totalRevenue > 0 ? (productionFee / totalRevenue) * 100 : 0;
      
      setSelectedMonthDetail({
        clients,
        contractors,
        totals: { totalRevenue, totalPayout, totalGigs, productionFee, margin }
      });
    } catch (err) {
      console.error(err);
    }
  };

  const navigateMonth = (direction) => {
    let newMonth = selectedMonth.month + direction;
    let newYear = selectedMonth.year;
    
    if (newMonth > 11) {
      newMonth = 0;
      newYear++;
    } else if (newMonth < 0) {
      newMonth = 11;
      newYear--;
    }
    
    setSelectedMonth({ month: newMonth, year: newYear });
  };

  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const fullMonthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  if (loading) return <div className="flex justify-center py-12"><div className="w-8 h-8 border-4 border-hub-200 border-t-hub-600 rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6 animate-fade-in">
      <h1 className="text-2xl font-bold">Reports</h1>

      {/* Month-over-Month Trend */}
      <ReportCard title="12-Month Trend" icon={TrendingUp}>
        <div className="mb-4">
          <SimpleBarChart 
            data={monthData} 
            valueKey="totalInvoice" 
            labelKey="month"
          />
        </div>
        <div className="overflow-x-auto">
          <table className="table text-sm">
            <thead>
              <tr>
                <th>Month</th>
                <th className="text-right">Gigs</th>
                <th className="text-right">Billed</th>
                <th className="text-right">Payouts</th>
                <th className="text-right">Prod. Fee</th>
                <th className="text-right">Margin</th>
              </tr>
            </thead>
            <tbody>
              {monthData.map((row, i) => (
                <tr key={i} className="hover:bg-gray-50 cursor-pointer" onClick={() => {
                  const [monthStr, yearStr] = row.month.split(' ');
                  const monthIndex = monthNames.indexOf(monthStr);
                  if (monthIndex >= 0) {
                    setSelectedMonth({ month: monthIndex, year: parseInt(yearStr) });
                  }
                }}>
                  <td className="font-medium">{row.month}</td>
                  <td className="text-right">{row.gigCount}</td>
                  <td className="text-right">{formatCurrency(row.totalInvoice)}</td>
                  <td className="text-right">{formatCurrency(row.totalPayout)}</td>
                  <td className="text-right">{formatCurrency(row.productionFee)}</td>
                  <td className={`text-right font-semibold margin-${getMarginStatus(row.marginPercent)}`}>
                    {formatPercent(row.marginPercent)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </ReportCard>

      {/* Client Concentration */}
      {clientConcentration && (
        <ReportCard title="Client Concentration" icon={PieChart}>
          {clientConcentration.highRiskClients > 0 && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-600" />
              <span className="text-red-700 text-sm font-medium">
                {clientConcentration.highRiskClients} client(s) represent over 50% of revenue - high concentration risk
              </span>
            </div>
          )}
          <div className="space-y-3">
            {clientConcentration.clients.map((client) => (
              <div key={client.clientId} className="flex items-center gap-3">
                <div className="w-32 text-sm font-medium truncate">{client.clientName}</div>
                <div className="flex-1 h-8 bg-gray-100 rounded-lg overflow-hidden relative">
                  <div 
                    className={`h-full rounded-lg transition-all duration-300 ${
                      client.riskLevel === 'high' ? 'bg-red-500' :
                      client.riskLevel === 'medium' ? 'bg-yellow-500' : 'bg-green-500'
                    }`}
                    style={{ width: `${Math.min(parseFloat(client.percentage), 100)}%` }}
                  />
                  <span className="absolute inset-0 flex items-center justify-center text-xs font-semibold text-gray-700">
                    {client.percentage}%
                  </span>
                </div>
                <div className="w-24 text-right text-sm">
                  {formatCurrency(client.revenue)}
                </div>
                {client.riskLevel === 'high' && (
                  <AlertTriangle className="w-4 h-4 text-red-500" />
                )}
              </div>
            ))}
          </div>
          <div className="mt-4 pt-4 border-t text-sm text-gray-500">
            Total: {formatCurrency(clientConcentration.totalRevenue)} across {clientConcentration.clientCount} clients
          </div>
        </ReportCard>
      )}

      {/* Selected Month Detail */}
      <div className="card">
        <div className="card-header flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-hub-600" />
            <h2 className="font-semibold">Monthly Detail</h2>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => navigateMonth(-1)} className="p-1 hover:bg-gray-100 rounded">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <span className="font-medium min-w-[140px] text-center">
              {fullMonthNames[selectedMonth.month]} {selectedMonth.year}
            </span>
            <button onClick={() => navigateMonth(1)} className="p-1 hover:bg-gray-100 rounded">
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
        
        {selectedMonthDetail && (
          <div className="card-body">
            {/* Month Summary */}
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mb-6 p-4 bg-gray-50 rounded-lg">
              <div>
                <p className="text-sm text-gray-500">Gigs</p>
                <p className="text-xl font-bold">{selectedMonthDetail.totals.totalGigs}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Billed</p>
                <p className="text-xl font-bold">{formatCurrency(selectedMonthDetail.totals.totalRevenue)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Payouts</p>
                <p className="text-xl font-bold">{formatCurrency(selectedMonthDetail.totals.totalPayout)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Production Fee</p>
                <p className="text-xl font-bold">{formatCurrency(selectedMonthDetail.totals.productionFee)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Margin</p>
                <p className={`text-xl font-bold margin-${getMarginStatus(selectedMonthDetail.totals.margin)}`}>
                  {formatPercent(selectedMonthDetail.totals.margin)}
                </p>
              </div>
            </div>

            {/* By Client & Contractor */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <h3 className="font-medium text-gray-700 mb-3 flex items-center gap-2">
                  <Building2 className="w-4 h-4" /> By Client
                </h3>
                {selectedMonthDetail.clients.length === 0 ? (
                  <p className="text-sm text-gray-500">No gigs this month</p>
                ) : (
                  <div className="space-y-2">
                    {selectedMonthDetail.clients.map((row) => (
                      <div key={row.clientId} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <p className="font-medium text-sm">{row.clientName}</p>
                          <p className="text-xs text-gray-500">{row.gigCount} gigs • {formatCurrency(row.totalInvoice)}</p>
                        </div>
                        <span className={`font-semibold margin-${getMarginStatus(row.marginPercent)}`}>
                          {formatPercent(row.marginPercent)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              <div>
                <h3 className="font-medium text-gray-700 mb-3 flex items-center gap-2">
                  <Users className="w-4 h-4" /> Contractor Costs
                </h3>
                {selectedMonthDetail.contractors.length === 0 ? (
                  <p className="text-sm text-gray-500">No contractor costs this month</p>
                ) : (
                  <div className="space-y-2">
                    {selectedMonthDetail.contractors.map((row) => (
                      <div key={row.contractorId} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <p className="font-medium text-sm">{row.contractorName}</p>
                          <p className="text-xs text-gray-500">{row.gigCount} gigs</p>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-sm">{formatCurrency(row.totalPayout)}</p>
                          <p className="text-xs text-gray-500">avg {formatCurrency(row.averagePayout)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
